<template lang="pug">
  .module__main
    .module__title Список кошельков
    .module-content__container
      .filters__container
        .dates__container
          .date__container
            el-date-picker(
              v-model="date"
              type="datetimerange"
              range-separator="-"
              start-placeholder="От"
              end-placeholder="До"
              :picker-options="dateOptions"
            )
          el-checkbox.only-balance__checkbox(v-model="onlyWithBalance" @change="reloadWallets") Только с балансом
          el-checkbox.only-balance__checkbox(v-model="onlyAccumulating" @change="reloadWallets") Накопительные
          el-checkbox.only-balance__checkbox(v-model="onlyFrozen" @change="reloadWallets") Замороженные
          .aml-max-filter(v-if="isSuperadmin")
            span.aml-max-filter__label Макс. AML, %
            el-select.aml-max-filter__control(
              v-model="amlMax"
              filterable
              allow-create
              clearable
              default-first-option
              placeholder="Не задано"
              @change="applyAmlMax"
            )
              el-option(
                v-for="value in amlMaxOptions"
                :key="`aml-max-${value}`"
                :label="`${value}`"
                :value="value"
              )
        .search__container
          el-select(v-model="searchMode" @change="updateDefaultSearchMode()")
            el-option(v-for="(mode, key) in searchModes" :key="`search-mode-${key}`" :label="mode.locale" :value="key")
          el-input(v-model="searchValue" :placeholder="searchModes[searchMode].title")
            i.el-input__icon.el-icon-search(slot="suffix" @click="searchOrder()")
          i.el-icon-close.search-reset__btn(v-if="searchLoaded" @click="resetSearch()")
      .control-buttons__container
        .new-wallet__container
          el-button(size="mini" @click="openCreateWallet") Создать новый
      .table__container
        .table-list__container
          el-table(
            v-loading="isLoading"
            :data="formattedWallets"
            :default-sort="walletDefaultSort"
            row-key="address"
            style="width: 100%; font-size: 12px"
            @sort-change="handleSortChange"
          )
            el-table-column(v-if="isAdmin || isSupport" prop="user" label="Пользователь" width="110")
            el-table-column(prop="address" label="Адрес" width="300")
            el-table-column(prop="description" label="Описание" width="200")
              template(slot-scope="scope")
                .wallet-description
                  span.wallet-description__text(v-if="scope.row.description") {{ scope.row.description }}
                  span.wallet-description__empty(v-else) Нет описания
                  el-tooltip(content="Редактировать описание" placement="top")
                    el-button.wallet-description__edit(
                      size="mini"
                      icon="el-icon-edit"
                      circle
                      @click="editDescription(scope.row)"
                    )
            el-table-column(prop="balance" label="Баланс" width="225" sortable="custom")
              template(slot-scope="scope")
                .wallet-balance
                  .wallet-balance__row.wallet-balance__row--available
                    span.wallet-balance__label Доступно
                    span.wallet-balance__value {{ formatBalance(scope.row.availableBalance) }}
                  .wallet-balance__row.wallet-balance__row--frozen
                    span.wallet-balance__label Заморожено
                    span.wallet-balance__value {{ formatBalance(scope.row.frozenBalance) }}
                  .wallet-balance__row.wallet-balance__row--unconfirmed
                    span.wallet-balance__label Не подтверждено
                    span.wallet-balance__value {{ formatBalance(scope.row.unconfirmedBalance) }}
            el-table-column(prop="status" label="Статус" width="140")
              template(slot-scope="scope")
                .wallet-status
                  el-tag(:type="walletStatusType(scope.row)" size="mini") {{ walletStatusText(scope.row) }}
                  el-tag(v-if="scope.row.accumulating" size="mini") Накопительный
            el-table-column(v-if="isSuperadmin" prop="amlScore" label="AML" width="150")
              template(slot-scope="scope")
                span(v-if="scope.row.amlStatus === 'not_applicable'") —
                span.aml-pending(v-else-if="scope.row.amlStatus !== 'ready'") На проверке
                aml-report-popover(
                  v-else-if="scope.row.amlReport"
                  :address="scope.row.address"
                  :report="scope.row.amlReport"
                  :score="scope.row.amlScore"
                  :risk-level="scope.row.amlRiskLevel"
                  :analysis-risk="scope.row.amlAnalysisRisk"
                )
                span.aml-score(
                  v-else
                  :style="amlRiskStyle(scope.row.amlAnalysisRisk)"
                  :title="scope.row.amlRiskLevel || ''"
                ) {{ formatAmlPercent(scope.row.amlScore) }}
            el-table-column(v-if="isSuperadmin" label="Действия" width="140")
              template(slot-scope="scope")
                .wallet-actions
                  el-button(
                    v-if="!scope.row.accumulating"
                    size="mini"
                    :type="scope.row.frozen ? 'warning' : 'info'"
                    @click="toggleFrozen(scope.row)"
                  ) {{ scope.row.frozen ? 'Разморозить' : 'Заморозить' }}
                  el-button(
                    size="mini"
                    type="primary"
                    :disabled="scope.row.manualAvailableBalance <= 0"
                    @click="openManualWithdraw(scope.row)"
                  ) Вывести
            el-table-column(prop="date" label="Дата" width="140")
        .table-pagination__container
          el-pagination(
            style="margin-top: 20px"
            v-if='hasPagination',
            :total='wallets.pagination.total',
            :page-size="per_page",
            :current-page='wallets.pagination.page',
            background,
            layout='prev, pager, next',
            @current-change='loadWallets',
          )
    el-dialog(
      title="Создание адреса"
      :visible.sync="isCreateWalletOpen"
      width="420px"
      @close="resetCreateWallet"
    )
      el-form(label-position="top")
        el-form-item(label="Тип адреса")
          el-switch(
            v-model="createWalletForm.accumulating"
            active-text="Накопительный"
            inactive-text="Обычный"
          )
        el-form-item(v-if="createWalletForm.accumulating" label="Ожидаемая сумма пополнения")
          el-input(v-model="createWalletForm.amount" type="number" min="0" step="0.00000001")
      span(slot="footer")
        el-button(@click="isCreateWalletOpen = false") Отмена
        el-button(type="primary" :loading="createWalletLoading" @click="getNewWallet") Создать
    el-dialog(
      title="Ручной вывод"
      :visible.sync="isManualWithdrawOpen"
      width="460px"
      @close="resetManualWithdraw"
    )
      .manual-withdraw__source {{ manualWithdraw.sourceAddress }}
      el-form(label-position="top")
        el-form-item(label="Адрес получателя")
          el-input(v-model="manualWithdraw.address")
        el-form-item(label="Сумма")
          .manual-withdraw__amount
            el-input(v-model="manualWithdraw.amount" type="number")
            el-button(
              size="mini"
              :disabled="manualWithdraw.maxAmount <= 0"
              @click="setManualWithdrawMax"
            ) Макс
        el-form-item(label="Код 2FA")
          el-input(v-model="manualWithdraw.token_2fa" placeholder="Код 2FA")
      span(slot="footer")
        el-button(@click="isManualWithdrawOpen = false") Отмена
        el-button(
          type="primary"
          :loading="manualWithdrawLoading"
          :disabled="!canSubmitManualWithdraw"
          @click="requestManualWithdraw"
        ) Создать
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import {
  CREATE_WALLET,
  LOAD_WALLETS_LIST,
  SET_WALLET_DESCRIPTION,
  SET_WALLET_FROZEN,
} from '@/store/const/wallets';
import { ADD_WITHDRAW } from '@/store/const/withdraws';
import instruments from '@/mixins/instruments';
import {
  loadListFilters,
  restoreDateRange,
  saveListFilters,
} from '@/utils/listFilters';
import AmlReportPopover from '@/components/AmlReportPopover.vue';
import {
  amlRiskFromLevel,
  amlRiskStyle,
  formatAmlPercent,
} from '@/utils/aml';

const FILTERS_STORAGE_KEY = 'wallets_filters';

export default {
  name: 'Wallets',
  components: { AmlReportPopover },
  mixins: [instruments],
  data() {
    const filters = loadListFilters(FILTERS_STORAGE_KEY);
    const searchValue =
      typeof filters.searchValue === 'string' ? filters.searchValue : '';
    const amlMax =
      Number.isFinite(filters.amlMax) &&
      filters.amlMax >= 0 &&
      filters.amlMax <= 100
        ? filters.amlMax
        : '';
    return {
      page: 1,
      per_page: 50,
      searchValue,
      searchLoaded: !!searchValue,
      searchMode: localStorage.getItem('wallets_searchMode') || 'address',
      intervalHandle: null,
      isFirstLoading: true,
      onlyWithBalance: filters.onlyWithBalance === true,
      onlyAccumulating: filters.onlyAccumulating === true,
      onlyFrozen: filters.onlyFrozen === true,
      amlMax,
      amlMaxOptions: [0, 20, 40, 60, 80, 100],
      balanceSort: ['asc', 'desc'].includes(filters.balanceSort)
        ? filters.balanceSort
        : '',
      isManualWithdrawOpen: false,
      isCreateWalletOpen: false,
      createWalletForm: {
        accumulating: false,
        amount: '',
      },
      manualWithdraw: {
        sourceAddress: '',
        address: '',
        amount: '',
        maxAmount: 0,
        token_2fa: '',
      },
      date: restoreDateRange(filters.date),
      dateOptions: {
        shortcuts: [
          {
            text: 'Сегодня',
            onClick(picker) {
              const start = new Date();
              const end = new Date();
              start.setHours(0, 0, 0, 0);
              end.setHours(23, 59, 59, 999);
              picker.$emit('pick', [start, end]);
            },
          },
          {
            text: 'Сутки',
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000 * 24);
              picker.$emit('pick', [start, end]);
            },
          },
          {
            text: 'Вчера',
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setDate(start.getDate() - 1);
              start.setHours(0, 0, 0, 0);
              end.setDate(end.getDate() - 1);
              end.setHours(23, 59, 59, 999);
              picker.$emit('pick', [start, end]);
            },
          },
          {
            text: 'Неделя',
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 7);
              picker.$emit('pick', [start, end]);
            },
          },
          {
            text: 'Месяц',
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 30);
              picker.$emit('pick', [start, end]);
            },
          },
          {
            text: '3 месяца',
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 90);
              picker.$emit('pick', [start, end]);
            },
          },
        ],
      },
    };
  },
  async mounted() {
    await this.loadWallets();
    this.isFirstLoading = false;
    this.startInterval();
  },
  beforeDestroy() {
    this.saveFilters();
    this.stopInterval();
  },
  computed: {
    ...mapGetters(['loading', 'error']),
    ...mapGetters('wallets', ['wallets']),
    ...mapGetters('user', ['isAdmin', 'isSuperadmin', 'isSupport', 'username']),
    isLoading() {
      return (
        this.isFirstLoading && this.loading(LOAD_WALLETS_LIST) === 'loading'
      );
    },
    isError() {
      return this.loading(LOAD_WALLETS_LIST) === 'error';
    },
    errorMessage() {
      return this.error(LOAD_WALLETS_LIST);
    },
    manualWithdrawLoading() {
      return this.loading(ADD_WITHDRAW) === 'loading';
    },
    createWalletLoading() {
      return this.loading(CREATE_WALLET) === 'loading';
    },
    canSubmitManualWithdraw() {
      return (
        !!this.manualWithdraw.sourceAddress &&
        !!this.manualWithdraw.address &&
        parseFloat(this.manualWithdraw.amount) > 0 &&
        !!this.manualWithdraw.token_2fa
      );
    },
    hasPagination() {
      return this.wallets.pagination.total > this.per_page;
    },
    walletDefaultSort() {
      return this.balanceSort
        ? {
            prop: 'balance',
            order: this.balanceSort === 'asc' ? 'ascending' : 'descending',
          }
        : {};
    },
    searchModes() {
      const modes = {
        address: { locale: 'По кошельку', title: 'Адрес кошелька' },
      };
      return modes;
    },
    dates() {
      if (Array.isArray(this.date)) {
        const from = new Date(this.date[0]);
        const to = new Date(this.date[1]);
        from.setMinutes(from.getMinutes() - from.getTimezoneOffset()); // SET TO UTC 0
        to.setMinutes(to.getMinutes() - to.getTimezoneOffset()); // SET TO UTC 0
        from.setMinutes(from.getMinutes() - 180); // SET TO UTC +3
        to.setMinutes(to.getMinutes() - 180); // SET TO UTC +3
        return {
          from: from.getTime(),
          to: to.getTime(),
        };
      } else {
        return null;
      }
    },
    formattedWallets() {
      return this.wallets.list.map((wallet) => {
        const date = new Date(wallet.createdAt);
        const availableBalance = wallet.availableBalance || 0;
        const frozenBalance = wallet.frozenBalance || 0;
        const unconfirmedBalance = wallet.unconfirmedBalance || 0;
        const manualAvailableBalance =
          wallet.manualAvailableBalance !== undefined
            ? wallet.manualAvailableBalance
            : availableBalance + frozenBalance;
        const formattedWallet = {
          ...wallet,
          description: wallet.description || '',
          balance:
            wallet.balance ||
            availableBalance + frozenBalance + unconfirmedBalance,
          availableBalance,
          frozenBalance,
          unconfirmedBalance,
          reservedBalance: wallet.reservedBalance || 0,
          manualAvailableBalance,
          confirmedBalance: availableBalance + frozenBalance,
          frozen: !!wallet.frozen,
          accumulating: !!wallet.accumulating,
          date: date.toLocaleString(),
        };
        if (this.isSuperadmin) {
          Object.assign(formattedWallet, {
            amlStatus: wallet.amlStatus || 'not_applicable',
            amlScore: wallet.amlScore === undefined ? 0 : wallet.amlScore,
            amlRiskLevel: wallet.amlRiskLevel || '',
            amlAnalysisRisk:
              wallet.amlAnalysisRisk ||
              amlRiskFromLevel(wallet.amlRiskLevel),
            amlReport: wallet.amlReport || null,
          });
        }
        return formattedWallet;
      });
    },
  },
  methods: {
    ...mapActions('wallets', {
      loadWalletsList: LOAD_WALLETS_LIST,
      createWallet: CREATE_WALLET,
      setWalletFrozen: SET_WALLET_FROZEN,
      setWalletDescription: SET_WALLET_DESCRIPTION,
    }),
    ...mapActions('withdraws', {
      addWithdraw: ADD_WITHDRAW,
    }),
    saveFilters() {
      saveListFilters(FILTERS_STORAGE_KEY, {
        searchValue: this.searchValue,
        date: this.date,
        onlyWithBalance: this.onlyWithBalance,
        onlyAccumulating: this.onlyAccumulating,
        onlyFrozen: this.onlyFrozen,
        amlMax: this.isSuperadmin ? this.amlMax : '',
        balanceSort: this.balanceSort,
      });
    },
    formatBalance(value) {
      return Number(value || 0).toFixed(8);
    },
    formatAmlPercent,
    amlRiskStyle,
    walletStatusText(wallet) {
      if (wallet.frozen) {
        return 'Заморожен';
      }
      if (wallet.unconfirmedBalance > 0) {
        return 'Не подтверждён';
      }
      return 'Подтверждён';
    },
    walletStatusType(wallet) {
      if (wallet.frozen) {
        return '';
      }
      if (wallet.unconfirmedBalance > 0) {
        return 'danger';
      }
      return 'success';
    },
    startInterval() {
      this.stopInterval();
      this.intervalHandle = setInterval(
        () => this.loadWallets(this.page),
        10000,
      );
    },
    stopInterval() {
      if (this.intervalHandle) {
        clearInterval(this.intervalHandle);
        this.intervalHandle = null;
      }
    },
    async loadWallets(page = 1) {
      if (this.isLoading) {
        return;
      }
      const params = {
        page,
        per_page: this.per_page,
      };
      if (this.searchValue) {
        params[this.searchMode] = this.searchValue;
      }
      if (this.date) {
        params.from = this.dates.from;
        params.to = this.dates.to;
      }
      if (this.onlyWithBalance) {
        params.only_with_balance = '1';
      }
      if (this.onlyAccumulating) {
        params.accumulating = '1';
      }
      if (this.onlyFrozen) {
        params.frozen = '1';
      }
      if (this.isSuperadmin && this.amlMax !== '') {
        params.aml_max = this.amlMax;
      }
      if (this.balanceSort) {
        params.sort = 'balance';
        params.order = this.balanceSort;
      }
      await this.loadWalletsList({ params });
      this.page = page;
      this.searchLoaded = !!this.searchValue;
    },
    async searchOrder() {
      this.stopInterval();
      this.searchValue = this.searchValue.trim();
      await this.loadWallets(1);
    },
    async reloadWallets() {
      this.stopInterval();
      await this.loadWallets(1);
      this.startInterval();
    },
    async applyAmlMax(value) {
      if (value === '' || value === null || value === undefined) {
        this.amlMax = '';
        return this.reloadWallets();
      }
      const amlMax = Number(String(value).replace(',', '.'));
      if (!Number.isFinite(amlMax) || amlMax < 0 || amlMax > 100) {
        this.amlMax = '';
        this.$message.warning('Максимальное значение AML должно быть от 0 до 100');
        return this.reloadWallets();
      }
      this.amlMax = amlMax;
      return this.reloadWallets();
    },
    async handleSortChange({ prop, order }) {
      this.balanceSort =
        prop === 'balance' && order
          ? order === 'ascending'
            ? 'asc'
            : 'desc'
          : '';
      await this.reloadWallets();
    },
    resetSearch() {
      this.searchLoaded = false;
      this.searchValue = '';
      this.reloadWallets();
    },
    updateDefaultSearchMode() {
      localStorage.setItem('wallets_searchMode', this.searchMode);
    },
    async toggleFrozen(wallet) {
      const frozen = !wallet.frozen;
      const action = frozen ? 'заморозить' : 'разморозить';
      this.$confirm(
        `Вы уверены, что хотите ${action} адрес ${wallet.address}?`,
        'Подтверждение',
        {
          confirmButtonText: frozen ? 'Заморозить' : 'Разморозить',
          cancelButtonText: 'Отмена',
        },
      )
        .then(async () => {
          const request = await this.setWalletFrozen({
            address: wallet.address,
            frozen,
          });
          await this.notifyRequestResult({
            request,
            successMessage: frozen ? 'Адрес заморожен' : 'Адрес разморожен',
            methodOnSuccess: () => this.loadWallets(this.page),
          });
        })
        .catch(() => {
          this.$message.info('Действие отменено');
        });
    },
    editDescription(wallet) {
      this.$prompt('Описание адреса', 'Описание', {
        inputValue: wallet.description || '',
        inputType: 'textarea',
        confirmButtonText: 'Сохранить',
        cancelButtonText: 'Отмена',
        inputValidator: (value) =>
          (value || '').length <= 500 || 'Не больше 500 символов',
      })
        .then(async ({ value }) => {
          const request = await this.setWalletDescription({
            address: wallet.address,
            description: value || '',
          });
          await this.notifyRequestResult({
            request,
            successMessage: 'Описание сохранено',
            methodOnSuccess: () => this.loadWallets(this.page),
          });
        })
        .catch(() => {});
    },
    openManualWithdraw(wallet) {
      this.manualWithdraw = {
        sourceAddress: wallet.address,
        address: '',
        amount: '',
        maxAmount: wallet.manualAvailableBalance || 0,
        token_2fa: '',
      };
      this.isManualWithdrawOpen = true;
    },
    setManualWithdrawMax() {
      this.manualWithdraw.amount = this.formatBalance(
        this.manualWithdraw.maxAmount,
      );
    },
    resetManualWithdraw() {
      this.manualWithdraw = {
        sourceAddress: '',
        address: '',
        amount: '',
        maxAmount: 0,
        token_2fa: '',
      };
    },
    async requestManualWithdraw() {
      const amount = parseFloat(this.manualWithdraw.amount);
      if (!this.manualWithdraw.address || !amount || amount <= 0) {
        return this.$message.warning('Укажите адрес получателя и сумму');
      }

      const request = await this.addWithdraw({
        sourceAddress: this.manualWithdraw.sourceAddress,
        address: this.manualWithdraw.address,
        token_2fa: this.manualWithdraw.token_2fa,
        amount,
      });
      await this.notifyRequestResult({
        request,
        successMessage: 'Ручной вывод создан',
        methodOnSuccess: async () => {
          this.isManualWithdrawOpen = false;
          await this.loadWallets(this.page);
        },
      });
    },
    openCreateWallet() {
      this.isCreateWalletOpen = true;
    },
    resetCreateWallet() {
      this.createWalletForm = { accumulating: false, amount: '' };
    },
    async getNewWallet() {
      const amount = Number(this.createWalletForm.amount);
      if (
        this.createWalletForm.accumulating &&
        (!Number.isFinite(amount) || amount <= 0)
      ) {
        return this.$message.warning('Укажите ожидаемую сумму пополнения');
      }
      const request = await this.createWallet(
        this.createWalletForm.accumulating
          ? { accumulating: true, amount }
          : {},
      );
      if (request.success) {
        this.copyTextToClipboard(request.data);
        this.isCreateWalletOpen = false;
      }
      await this.notifyRequestResult({
        request,
        successMessage: 'Кошелек успешно создан и скопирован в буфер обмена',
        methodOnSuccess: this.loadWallets,
      });
    },
  },
};
</script>
<style scoped>
.filters__container {
  margin-bottom: 5px;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  border: 1px solid #c9c9c9;
}

.search__container {
  display: flex;
  justify-content: center;
  align-items: flex-end;
  margin-left: auto;
}

.search-reset__btn {
  padding: 11px;
  background-color: #e95050;
  cursor: pointer;
}

.search-reset__btn:hover {
  padding: 10px;
  background-color: #dd3636;
  border: 1px solid #850202;
}

.search__container .el-select {
  width: 250px;
}

.dates__container {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  row-gap: 6px;
  padding: 4px 0;
}

.date__container {
  margin: 0 5px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.only-balance__checkbox {
  margin-left: 10px;
}

.aml-max-filter {
  display: flex;
  align-items: center;
  margin-left: 14px;
  white-space: nowrap;
}

.aml-max-filter__label {
  margin-right: 6px;
}

.aml-max-filter__control {
  width: 120px;
}

.wallet-description {
  display: flex;
  align-items: center;
  max-width: 500px;
}

.wallet-description__text {
  flex: 1;
  word-break: break-word;
}

.wallet-description__empty {
  flex: 1;
  color: #909399;
}

.wallet-description__edit {
  margin-left: 6px;
}

.wallet-balance {
  display: grid;
  gap: 3px;
  min-width: 190px;
}

.wallet-balance__row {
  display: grid;
  grid-template-columns: 100px 1fr;
  align-items: center;
}

.wallet-balance__label {
  color: #606266;
}

.wallet-balance__value {
  text-align: right;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.wallet-balance__row--available .wallet-balance__value {
  color: #67c23a;
}

.wallet-balance__row--frozen .wallet-balance__value {
  color: #409eff;
}

.wallet-balance__row--unconfirmed .wallet-balance__value {
  color: #f56c6c;
}

.control-buttons__container {
  margin-bottom: 5px;
}

.wallet-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.wallet-status {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.manual-withdraw__source {
  margin-bottom: 10px;
  font-size: 12px;
  color: #606266;
  word-break: break-all;
}

.manual-withdraw__amount {
  display: flex;
  gap: 6px;
}

.aml-score,
.aml-pending {
  display: inline-block;
  min-width: 64px;
  padding: 4px 8px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  text-align: center;
}

.aml-pending {
  color: #606266;
  background: #f4f4f5;
}
</style>
